import { Theme } from "@mui/material";
import { SystemStyleObject } from "@mui/system";

/** Plain style objects (spreadable), unlike the SxProps union. */
type Sx = SystemStyleObject<Theme>;

/** The editor's palette: the project page's own chrome colours. */
export const BG = "#212b37";
export const BG_FLOAT = "rgba(33,43,55,0.97)";
export const LINE = "rgba(255,255,255,0.1)";
export const LINE_STRONG = "rgba(255,255,255,0.18)";
export const TEXT = "#fff";
export const MUTED = "rgba(255,255,255,0.62)";
export const FAINT = "rgba(255,255,255,0.4)";
export const TEAL = "#00d4ad";
export const TEAL_INK = "#06302a";
export const TEAL_HOVER = "#19e0bb";
export const AMBER = "#f5a623";
export const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

/** A floating panel over the map. */
export const floatSx: Sx = {
  bgcolor: BG_FLOAT, border: `1px solid ${LINE_STRONG}`, borderRadius: 3,
  boxShadow: "0 12px 34px rgba(0,0,0,0.42)", color: TEXT, pointerEvents: "auto",
};

/** Small uppercase section label. */
export const capSx: Sx = { fontSize: 10.5, fontWeight: 600, letterSpacing: 0.9, textTransform: "uppercase", color: FAINT };

/** Buttons: primary teal, plain, ghost, accept, reject. */
export const btnBase: Sx = {
  textTransform: "none", fontWeight: 500, fontSize: 13, height: 32, px: 1.5, borderRadius: 1.5, minWidth: 0,
  whiteSpace: "nowrap", lineHeight: 1,
};
export const btnPlain: Sx = {
  ...btnBase, color: TEXT, bgcolor: "rgba(255,255,255,0.07)", border: `1px solid ${LINE_STRONG}`,
  "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
  "&.Mui-disabled": { color: FAINT, borderColor: LINE },
};
export const btnPrimary: Sx = {
  ...btnBase, fontWeight: 600, color: TEAL_INK, bgcolor: TEAL, border: `1px solid ${TEAL}`,
  "&:hover": { bgcolor: TEAL_HOVER, borderColor: TEAL_HOVER },
  "&.Mui-disabled": { bgcolor: "rgba(0,212,173,0.35)", borderColor: "transparent", color: "rgba(6,48,42,0.7)" },
};
export const btnGhost: Sx = {
  ...btnBase, color: MUTED, bgcolor: "transparent", border: "1px solid transparent",
  "&:hover": { bgcolor: "rgba(255,255,255,0.08)", color: TEXT },
};
export const btnAccept: Sx = {
  ...btnBase, color: "#9ff0d4", bgcolor: "rgba(46,158,91,0.22)", border: "1px solid rgba(46,158,91,0.55)",
  "&:hover": { bgcolor: "rgba(46,158,91,0.32)" }, "&.Mui-disabled": { color: FAINT, borderColor: LINE },
};
export const btnReject: Sx = {
  ...btnBase, color: "#ffc2a6", bgcolor: "rgba(217,83,79,0.18)", border: "1px solid rgba(217,83,79,0.5)",
  "&:hover": { bgcolor: "rgba(217,83,79,0.28)" }, "&.Mui-disabled": { color: FAINT, borderColor: LINE },
};
export const btnSmall: Sx = { height: 26, px: 1.1, fontSize: 12 };

/** A radio-style option card. */
export const cardSx = (on: boolean): Sx => ({
  display: "flex", alignItems: "flex-start", gap: 1.25, width: "100%", p: "10px 12px", borderRadius: 2, cursor: "pointer",
  border: `1px solid ${on ? TEAL : LINE_STRONG}`, bgcolor: on ? "rgba(0,212,173,0.1)" : "rgba(255,255,255,0.04)",
  textAlign: "left", color: TEXT, "&:hover": { borderColor: on ? TEAL : "rgba(255,255,255,0.3)" },
});
export const radioSx = (on: boolean): Sx => ({
  flex: "0 0 16px", width: 16, height: 16, borderRadius: "50%", mt: "1px", border: `1.5px solid ${on ? TEAL : "rgba(255,255,255,0.4)"}`,
  display: "flex", alignItems: "center", justifyContent: "center",
  "&::after": { content: '""', width: 8, height: 8, borderRadius: "50%", bgcolor: on ? TEAL : "transparent" },
});

/** Status pill. */
export const pillSx = (tone: "plain" | "ok" | "run" = "plain"): Sx => ({
  display: "inline-flex", alignItems: "center", gap: 0.6, height: 22, px: 1, borderRadius: 11, fontSize: 11.5, fontWeight: 500, whiteSpace: "nowrap",
  bgcolor: tone === "ok" ? "rgba(46,158,91,0.25)" : tone === "run" ? "rgba(90,169,236,0.22)" : "rgba(255,255,255,0.1)",
  color: tone === "ok" ? "#9ff0d4" : tone === "run" ? "#a9cdfd" : "rgba(255,255,255,0.8)",
});

/** Keyboard hint. */
export const kbdSx: Sx = {
  display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 18, height: 18, px: 0.5, borderRadius: 1,
  border: `1px solid ${LINE_STRONG}`, bgcolor: "rgba(0,0,0,0.25)", color: MUTED, fontFamily: MONO, fontSize: 10.5, fontWeight: 500, lineHeight: 1,
};

/** Square tool button on the left rail. */
export const railBtnSx = (on: boolean): Sx => ({
  width: 40, height: 40, borderRadius: 2, color: on ? TEAL_INK : "rgba(255,255,255,0.85)", bgcolor: on ? TEAL : "transparent",
  "&:hover": { bgcolor: on ? TEAL_HOVER : "rgba(255,255,255,0.1)", color: on ? TEAL_INK : TEXT },
  "&.Mui-disabled": { color: FAINT },
});

/** A swatch square. */
export const swatchSx = (colour: string): Sx => ({ width: 10, height: 10, borderRadius: 0.75, flex: "0 0 10px", bgcolor: colour });

/** Text inputs and selects in the editor. */
export const inputSx: Sx = {
  fontSize: 12.5, color: TEXT, bgcolor: "rgba(0,0,0,0.22)", borderRadius: 1.5,
  ".MuiOutlinedInput-notchedOutline": { borderColor: LINE_STRONG },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.3)" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: TEAL },
  ".MuiSvgIcon-root": { color: MUTED },
};
export const menuItemSx: Sx = { fontSize: 12.5 };

/** The amber "needs a choice" callout. */
export const calloutSx: Sx = { p: "10px 12px", borderRadius: 2, bgcolor: "rgba(245,166,35,0.12)", border: "1px solid rgba(245,166,35,0.4)", color: "#f3dcae", fontSize: 12.5, lineHeight: 1.45 };
